import React, { useEffect, useState } from "react";
import { Skeleton, Typography, Box } from "@mui/material";
import { toPrice } from "../../helpers/stringFormat";
import Stack from "@mui/material/Stack";
import Divider from '@mui/material/Divider';

function BalanceHeaderV3(props) {
  const [balances, setBalances] = useState(props.balancesByPurpose || {});
  const [cols, setCols] = useState([]);
  const [renderedCols, setRenderedCols] = useState(<></>);
  const [bankAccountPurposes, setBankAccountPurposes] = useState(
    JSON.parse(window.localStorage.getItem("bankAccountPurposes")) || []
  );

  function createCol(entry, value) {
    const isSummary = entry.id === undefined;

    return {
      value,
      element: (
        <Box
          sx={{
            flexDirection: "column",
            minHeight: "50px",
            minWidth: "100px",
          }}
        >
          <Typography
            variant="overline"
            fontSize={12}
            sx={{ mb: 0, mr: 2 }}
            color="secondary"
            noWrap={true}
          >
            {entry.name}
          </Typography>

          {(isSummary || (props.isComplete && props.isComplete[entry.id])) ? (
            <Typography
              variant={"h4"}
              color={value >= 0 ? "primary" : ""}
              sx={{ mt: -1, mr: 2 }}
            >
              {toPrice(value)}
            </Typography>
          ) : (
            <Skeleton width={100} height={24} sx={{ mt: -1, mr: 2 }} />
          )}
        </Box>
      ),
    };
  }

  useEffect(() => {
    if (props.balancesByPurpose !== undefined) {
      setBalances(props.balancesByPurpose);
    } else if (props.connectedAccounts !== undefined) {
      // In connectedAccounts mode: only show total assets / liabilities
      let totalAssets = 0;
      let totalLiabilities = 0;

      props.connectedAccounts.forEach((account) => {
        const balance = account?.account?.aggregated_balance_raw;

        if (typeof balance === "number") {
          if (balance >= 0) {
            totalAssets += balance;
          } else {
            totalLiabilities += balance;
          }
        }
      });

      const summary = {};
      if (totalAssets > 0 && totalLiabilities < 0) {
        summary["assets"] = totalAssets;
        summary["liabilities"] = totalLiabilities;
      } else {
        summary["total"] = totalAssets + totalLiabilities;
      }

      setBalances(summary);
    } else {
      const calculateBalances = (arr) => {
        const balancesByPurposeCalc = {};
        bankAccountPurposes.forEach((purpose) => {
          balancesByPurposeCalc[purpose.id] = 0;
        });
        arr.forEach((item) => {
          if (item === undefined) return;
          Object.entries(item.balances).forEach(([key, value]) => {
            balancesByPurposeCalc[key] += value;
          });
        });
        return balancesByPurposeCalc;
      };
      const properties = props.properties || [];
      const portfolios = props.portfolios || [];
      setBalances(calculateBalances([...properties, ...portfolios]));
    }
  }, [props.balancesByPurpose, props.properties, props.portfolios, props.connectedAccounts, bankAccountPurposes]);


  useEffect(() => {
    let purposeCols = [];

    Object.entries(balances).forEach(([key, value]) => {
      const matchedPurpose = bankAccountPurposes.find(
        (purpose) => purpose.id === Number(key)
      );
      if (!matchedPurpose) return;

      const displayValue =
        value && typeof value === "object" && value.aggregated_balance !== undefined
          ? value.aggregated_balance
          : value;

      const col = createCol(matchedPurpose, displayValue);
      if (col) purposeCols.push(col);
    });

    // Sort by absolute value
    purposeCols.sort((a, b) => Math.abs(b.value) - Math.abs(a.value));

    // 🔽 Summary Calculation
    let totalAssets = 0;
    let totalLiabilities = 0;

    Object.entries(balances).forEach(([_, value]) => {
      const amount =
        value && typeof value === "object" && value.aggregated_balance !== undefined
          ? value.aggregated_balance
          : value;

      if (amount >= 0) {
        totalAssets += amount;
      } else {
        totalLiabilities += amount;
      }
    });

    const summaryCols = [];

    if (totalAssets > 0 && totalLiabilities < 0) {
      summaryCols.push(createCol({ name: "Total Assets" }, totalAssets));
      summaryCols.push(createCol({ name: "Total Liabilities" }, totalLiabilities));
    } else {
      summaryCols.push(createCol({ name: "Total" }, totalAssets + totalLiabilities));
    }

    const updatedCols =
      purposeCols.length > 0
        ? [
          ...summaryCols,
          {
            value: null,
            element: (
              <Divider
                orientation="vertical"
                flexItem
                sx={{ mx: 2, borderColor: "grey.300" }}
              />
            ),
          },
          ...purposeCols,
        ]
        : [...summaryCols];

    setCols(updatedCols);
  }, [balances, bankAccountPurposes]);



  useEffect(() => {
    // Separate summary and regular columns
    const summaryCols = cols.filter(
      (col) =>
        col &&
        (col.element?.props?.children?.[0]?.props?.children === "Total" ||
          col.element?.props?.children?.[0]?.props?.children === "Total Assets" ||
          col.element?.props?.children?.[0]?.props?.children === "Total Liabilities")
    );

    const mainCols = cols.slice(summaryCols.length + 1); // +1 for dividerCol

    setRenderedCols(
      <Box sx={{ display: "flex", width: "100%", mt: 1 }}>
        {/* Fixed Summary + Conditional Divider */}
        <Stack direction="row" spacing={2}>
          {summaryCols.map((entry, index) => (
            <React.Fragment key={`summary-${index}`}>{entry.element}</React.Fragment>
          ))}

          {/* Render divider only if there are purpose-based mainCols */}
          {mainCols.length > 0 && (
            <Divider
              orientation="vertical"
              variant="middle"
              flexItem
              sx={{ mx: 2, borderColor: "grey.300" }}
            />
          )}
        </Stack>

        {/* Scrollable Main Columns */}
        {mainCols.length > 0 && (
          <Box
            sx={{
              display: "flex",
              overflowX: "auto",
              ml: 2,
            }}
          >
            <Stack direction="row" spacing={2}>
              {mainCols.map((entry, index) => (
                <React.Fragment key={`main-${index}`}>{entry.element}</React.Fragment>
              ))}
            </Stack>
          </Box>
        )}
      </Box>
    );
  }, [cols]);


  return (
    <>
      {renderedCols}
    </>
  );
}

export default BalanceHeaderV3;
